import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';
import useFlowsStore from '../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../util/autosave/nodedata_autosave';

interface InputSize {
  width: string;
  height: string;
}

const parsePxValue = (value: string): number | null => {
  const m = /^(\d+(?:\.\d+)?)px$/i.exec(value?.trim() ?? '');
  if (!m) {
    return null;
  }
  return Math.round(Number(m[1]));
};

const sizesCloseEnough = (
  measured: InputSize,
  saved: InputSize | undefined,
): boolean => {
  if (!saved?.width?.trim() || !saved?.height?.trim()) {
    return false;
  }
  const mw = parsePxValue(measured.width);
  const mh = parsePxValue(measured.height);
  const sw = parsePxValue(saved.width);
  const sh = parsePxValue(saved.height);
  if (mw === null || mh === null || sw === null || sh === null) {
    return measured.width === saved.width && measured.height === saved.height;
  }
  return Math.abs(mw - sw) <= 2 && Math.abs(mh - sh) <= 2;
};

interface UseInputSizeTrackingOptions {
  nodeId: string;
  inputSize?: InputSize;
  collapsed?: boolean;
  edges: any[];
  flowId?: string;
  setNodeState: any;
  setSaving: any;
  setSchema: any;
  sizeKey?: string;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  textareaName?: string;
}

const getElementSize = (element: HTMLTextAreaElement): InputSize => {
  const w = element.style.width?.trim() ?? '';
  const h = element.style.height?.trim() ?? '';
  return {
    width: w && !w.includes('%') ? w : `${element.offsetWidth}px`,
    height: h && !h.includes('%') ? h : `${element.offsetHeight}px`,
  };
};

const applyInitialSize = (
  element: HTMLTextAreaElement,
  savedInputSize: InputSize | undefined,
  hasAppliedInitialSizeRef: React.MutableRefObject<boolean>,
) => {
  if (!hasAppliedInitialSizeRef.current && savedInputSize) {
    if (savedInputSize.width) {
      element.style.width = savedInputSize.width;
    }
    if (savedInputSize.height) {
      element.style.height = savedInputSize.height;
    }
    hasAppliedInitialSizeRef.current = true;
  }
};

const saveSizeIfChanged = (
  size: InputSize,
  savedInputSize: InputSize | undefined,
  saveParams: {
    edges: any[];
    nodeId: string;
    flowId?: string;
    setNodeState: any;
    setSaving: any;
    setSchema: any;
    sizeKey: string;
    canPersistSize?: () => boolean;
  },
) => {
  if (!saveParams.nodeId?.trim()) {
    return;
  }
  if (size.width.includes('%') || size.height.includes('%')) {
    return;
  }
  if (sizesCloseEnough(size, savedInputSize)) {
    return;
  }
  if (
    size.width !== savedInputSize?.width ||
    size.height !== savedInputSize?.height
  ) {
    if (useFlowsStore.getState().flowCanvasPhase !== 'ready') {
      return;
    }
    if (saveParams.canPersistSize && !saveParams.canPersistSize()) {
      return;
    }
    nodeDataAutoSaveDynamic({
      newEdges: saveParams.edges,
      setNodeState: saveParams.setNodeState,
      id: saveParams.nodeId,
      setSaving: saveParams.setSaving,
      flowId: saveParams.flowId,
      objectData: { [saveParams.sizeKey]: size },
      setSchema: saveParams.setSchema,
    });
  }
};

export const useInputSizeTracking = ({
  nodeId,
  inputSize: savedInputSize,
  collapsed = false,
  edges,
  flowId,
  setNodeState,
  setSaving,
  setSchema,
  sizeKey = 'inputSize',
  textareaRef,
  textareaName,
}: UseInputSizeTrackingOptions): InputSize | undefined => {
  const flowLoadGeneration = useFlowsStore(s => s.flowLoadGeneration);
  const [inputSize, setInputSize] = useState(savedInputSize);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isResizingRef = useRef(false);
  const elementRef = useRef<HTMLTextAreaElement | null>(null);
  const hasAppliedInitialSizeRef = useRef(false);
  const lastNodeIdRef = useRef<string | null>(null);
  const canPersistSizeRef = useRef(false);
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  useEffect(() => {
    canPersistSizeRef.current = false;
    const t = setTimeout(() => {
      canPersistSizeRef.current = true;
    }, 1600);
    return () => {
      clearTimeout(t);
    };
  }, [nodeId, flowLoadGeneration, collapsed]);

  const canPersistSize = useCallback(() => canPersistSizeRef.current, []);

  useEffect(() => {
    if (savedInputSize) {
      setInputSize(savedInputSize);
    }
  }, [savedInputSize]);

  useEffect(() => {
    if (collapsed) return;
    if (!nodeId?.trim()) return;

    if (lastNodeIdRef.current !== nodeId) {
      hasAppliedInitialSizeRef.current = false;
      lastNodeIdRef.current = nodeId;
    }

    const findTextarea = (): HTMLTextAreaElement | null => {
      if (textareaRef?.current) {
        return textareaRef.current;
      }

      const selector = textareaName
        ? `[data-id="${nodeId}"] textarea[name="${textareaName}"]`
        : `[data-id="${nodeId}"] textarea`;
      return document.querySelector(selector) as HTMLTextAreaElement;
    };

    const createResizeCallback = (element: HTMLTextAreaElement) => {
      return () => {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        isResizingRef.current = true;
        const currentSize = getElementSize(element);
        setInputSize(currentSize);

        resizeTimeoutRef.current = setTimeout(() => {
          if (isResizingRef.current) {
            const finalSize = getElementSize(element);
            saveSizeIfChanged(finalSize, savedInputSize, {
              edges: edgesRef.current,
              nodeId,
              flowId,
              setNodeState,
              setSaving,
              setSchema,
              sizeKey,
              canPersistSize,
            });
            isResizingRef.current = false;
          }
        }, 500);
      };
    };

    const setupObserver = (element: HTMLTextAreaElement) => {
      elementRef.current = element;
      applyInitialSize(element, savedInputSize, hasAppliedInitialSizeRef);

      const onResize = createResizeCallback(element);
      let skipFirstResizeObservation = true;
      const resizeObserver = new ResizeObserver(() => {
        if (skipFirstResizeObservation) {
          skipFirstResizeObservation = false;
          return;
        }
        onResize();
      });
      resizeObserver.observe(element);
      return resizeObserver;
    };

    const initializeObserver = () => {
      const textarea = findTextarea();
      if (textarea) {
        return setupObserver(textarea);
      }
      return null;
    };

    let resizeObserver: ResizeObserver | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    resizeObserver = initializeObserver();
    if (!resizeObserver) {
      timeoutId = setTimeout(() => {
        resizeObserver = initializeObserver();
      }, 100);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      elementRef.current = null;
      isResizingRef.current = false;
    };
  }, [
    nodeId,
    collapsed,
    textareaRef,
    textareaName,
    savedInputSize,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
    sizeKey,
    canPersistSize,
  ]);

  return inputSize || savedInputSize;
};
